import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import type {
    BymoPagePropsContentSettingsMetadata
} from '../../client-server-common/bymo-page-props-metadata/bymo-page-props-metadata';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    BymoPagePropsSpecialContentSettingMetadata
} from '../../client-server-common/bymo-page-props-metadata/bymo-page-props-metadata';
import type {DoppeActionPrice} from '../../client-server-common/types/doppe-action-price';
import {
    doppeActionCtaButtonsResolveMainURL
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';
import type {CurrencyCode, RichTextContent, HideableValue } from '@wix/devzai-utils-common';
import {arrayRemoveNullValues, asArray, immerProduce, hideableValueCreateHidden, hideableValueGetVisibleValue, hideableValueIsVisibleAndNotEqualValue } from '@wix/devzai-utils-common';
import type {WixMediaResource} from '@wix/devzai-common-wix';
import {wixMediaResourceReplaceExternalImage} from '@wix/devzai-common-wix';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import {
    doppeActionCtaButtonsResetSettingsForLock, DoppeLinkSpecEntityType, DoppeLinkSpecType
} from '../../client-server-common/types/doppe-action-cta-button';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {
    doppeActionContentSectionSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import type {DoppeActionMediaViewSettings} from '../../client-server-common/types/doppe-action-media-view-settings';
import {
    doppeActionGetMediaViewDefaultSettingsMigration
} from '../../client-server-common/types/doppe-action-media-view-settings';
import {
    DoppeExternalMembershipPlanProvider,
    doppeExternalMembershipPlanProviderResolveBusinessEntityReference
} from '../../client-server-common/types/doppe-external-membership-plans';
import type {DoppeDtoMembershipPlan} from '../../client-server-common/types/doppe-dto-membership-plan';

export namespace DoppeActionTypeHoppPricingPlan {

    export interface BaseActionSettings extends DoppeActionMediaViewSettings {
        pricingPlanPageLink: string;
        pricingPlanTitle: HideableValue<string>;
        description: HideableValue<RichTextContent>;
        pricingPlanImages: WixMediaResource[] | null;
        priceData: HideableValue<DoppeActionPrice>;
        pricingPlanBenefits: string[];
        couponCode: HideableValue<string>;
        ribbon: HideableValue<string>;
        pricingPlanProvider: DoppeExternalMembershipPlanProvider | null;
        pricingPlanProviderId: string | null;
    }

    export interface ActionSettings extends
        BaseActionSettings,
        DoppeActionContentSectionsSettings,
        DoppeActionCtaButtonsSettings {
    }
}

export const doppeActionTypeHoppPricingPlanActionContentSettingsMetadata = {
    pricingPlanPageLink: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Url, BymoPagePropsContentSettingSemanticMeaning.EntityPageUrl),
    pricingPlanTitle: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
    description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
    pricingPlanImages: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.MultipleMedia, BymoPagePropsContentSettingSemanticMeaning.EntityMedia),
    priceData: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
    pricingPlanBenefits: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    couponCode: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    ribbon: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityRibbon)
} satisfies BymoPagePropsContentSettingsMetadata<DoppeActionTypeHoppPricingPlan.ActionSettings>

export const doppeActionTypeHoppPricingPlan = doppeActionTypeDefineMetadata<DoppeActionTypeHoppPricingPlan.ActionSettings>({
    id: 'io.bymo.action/hopp-pricing-plan',
    resolveActionSettings: action => {
        const { ...restSettings } = action.settings;
        return {
            pricingPlanPageLink: '',
            pricingPlanTitle: hideableValueCreateHidden(''),
            description: hideableValueCreateHidden(''),
            pricingPlanImages: null,
            priceData: hideableValueCreateHidden({ 
                actualPrice: 0,
                discountPrice: undefined,
                currencyCode: 'USD'
            }),
            pricingPlanBenefits: [],
            couponCode: hideableValueCreateHidden(''),
            ribbon: hideableValueCreateHidden(''),
            pricingPlanProvider: DoppeExternalMembershipPlanProvider.Hopp,
            pricingPlanProviderId: null,
            ...doppeActionGetMediaViewDefaultSettingsMigration(action.settings),
            ...doppeActionContentSectionSettingsDefaultValuesMigration(action.settings),
            ...doppeActionCtaButtonsSettingsDefaultValuesMigration(action.settings, false),
            ...restSettings
        }
    },
    actionSettingsMetadata: {
        contentSettingsMetadata: doppeActionTypeHoppPricingPlanActionContentSettingsMetadata,
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: { customContentSectionsTypes: {} },
        hideableContentSettingsNames: {
                couponCode: true,
                description: true,
                priceData: true,
                pricingPlanTitle: true,
                ribbon: true
            }
    },
    actionListItemSettingsMetadata: false,
    isWidgetOpener: true,
    isLinkOpener: true,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,

    resolveUsedMedia: actionSettings => {
        return arrayRemoveNullValues(asArray(hideableValueGetVisibleValue(actionSettings.pricingPlanImages, []) ?? []));
    },
    resolveMainMedia: actionSettings => {
        const mediaItems = arrayRemoveNullValues(asArray(hideableValueGetVisibleValue(actionSettings.pricingPlanImages, []) ?? []));
        return mediaItems[0] ?? null;
    },
    resolveMainLink: (actionSettings, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        return doppeActionCtaButtonsResolveMainURL(actionSettings, {
            sourceLinkSpec: actionSettings.pricingPlanProviderId ? {
                type: DoppeLinkSpecType.Entity,
                entityType: DoppeLinkSpecEntityType.PricingPlan,
                entityId: actionSettings.pricingPlanProviderId
            } : null,
            viewerLang: viewerLang,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager,
            bymoPageContextProps: bymoPageContextProps
        }) ?? null;
    },
    resolveCouponData: actionSettings => {
        return {
            showCouponCode: hideableValueIsVisibleAndNotEqualValue(actionSettings.couponCode, ''),
            couponCode: hideableValueGetVisibleValue(actionSettings.couponCode, ''),
            couponDescription: '',
            couponTitle: hideableValueGetVisibleValue(actionSettings.pricingPlanTitle, ''),
        }
    },
    resolveUsedBusinessEntities: actionSettings => {
        return arrayRemoveNullValues([
            actionSettings.pricingPlanProvider !== null && actionSettings.pricingPlanProviderId !== null ?
                doppeExternalMembershipPlanProviderResolveBusinessEntityReference(
                    actionSettings.pricingPlanProvider,
                    actionSettings.pricingPlanProviderId
                ) :
                null
        ])
    },
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) => {
        return immerProduce(actionSettings, actionSettings => {
            if (actionSettings.pricingPlanImages) {
                actionSettings.pricingPlanImages = actionSettings.pricingPlanImages.map(
                    image => wixMediaResourceReplaceExternalImage(image, externalImagesUrlToImageResourceMap))
            }
        })
    },
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
});

export function doppeActionTypeHoppPricingPlanResolveSettingsFromBoundEntity(
    actionSettings: DoppeActionTypeHoppPricingPlan.BaseActionSettings,
    pricingPlan: DoppeDtoMembershipPlan
): DoppeActionTypeHoppPricingPlan.BaseActionSettings {

    return {
        ...actionSettings,
        ribbon: hideableValueCreateHidden(''),
        pricingPlanImages: pricingPlan.media ? [pricingPlan.media] : null,
        pricingPlanPageLink: pricingPlan.membershipPageUrl ?? actionSettings?.pricingPlanPageLink ?? ``,
        priceData: pricingPlan.pricing ? {
            actualPrice: parseFloat(pricingPlan.pricing.price?.value ?? '0') || 0,
            currencyCode: (pricingPlan.pricing.price?.currency ?? 'USD') as CurrencyCode
        } : actionSettings?.priceData ?? hideableValueCreateHidden({
            actualPrice: 0,
            discountPrice: undefined,
            currencyCode: 'USD'
        }),
        pricingPlanBenefits: pricingPlan.perks ?? actionSettings?.pricingPlanBenefits ?? [],
        pricingPlanTitle: pricingPlan?.name ?? hideableValueCreateHidden(''),
        description: pricingPlan?.description ?? hideableValueCreateHidden(''),
    }
}

export function convertDoppeDtoMembershipPlanToHoppPricingPlanSettings(options: {
    pricingPlan: DoppeDtoMembershipPlan,
    pricingPlanSettings?: DoppeActionTypeHoppPricingPlan.ActionSettings
}) {
    const { pricingPlan, pricingPlanSettings = {} } = options;
    return {
        ...doppeActionContentSectionSettingsDefaultValuesMigration(pricingPlanSettings),
        ...doppeActionCtaButtonsSettingsDefaultValuesMigration({ ctaButtons: [] }, false),
        ...pricingPlanSettings,
        ...doppeActionGetMediaViewDefaultSettingsMigration(pricingPlanSettings),
        pricingPlanPageLink: pricingPlan.membershipPageUrl ?? '',
        pricingPlanTitle: hideableValueCreateHidden(pricingPlan.name ?? ''),
        description: hideableValueCreateHidden(pricingPlan.description ?? ''),
        pricingPlanImages: pricingPlan.media ? [pricingPlan.media] : null,
        priceData: pricingPlan.pricing ? hideableValueCreateHidden({
            actualPrice: parseFloat(pricingPlan.pricing.price?.value ?? '0') || 0,
            currencyCode: (pricingPlan.pricing.price?.currency ?? 'USD') as CurrencyCode
        }) : hideableValueCreateHidden({
            actualPrice: 0,
            discountPrice: undefined,
            currencyCode: 'USD' as CurrencyCode
        }),
        pricingPlanBenefits: pricingPlan.perks ?? [],
        couponCode: hideableValueCreateHidden(''),
        ribbon: hideableValueCreateHidden(''),
        pricingPlanProvider: DoppeExternalMembershipPlanProvider.Hopp,
        pricingPlanProviderId: pricingPlan.id,
    } satisfies DoppeActionTypeHoppPricingPlan.ActionSettings
} 